import { ReactNode } from 'react'

import { Frequency, TriggerDelayType } from 'src/graphql/types/automatedRules'
import IconClock from 'src/icons/IconClock'

const ICON_CLASS = 'h-3 w-3 fill-none stroke-current'

const DELAY_UNIT_LABEL: Record<string, { long: string }> = {
  [TriggerDelayType.MINUTE]: { long: 'minute' },
  [TriggerDelayType.HOUR]: { long: 'hour' },
  [TriggerDelayType.DAY]: { long: 'day' },
  [TriggerDelayType.WEEK]: { long: 'week' },
  [TriggerDelayType.MONTH]: { long: 'month' },
}

const FREQUENCY_UNIT_LABEL: Record<string, { adverb: string; long: string }> = {
  [Frequency.MINUTE]: { adverb: 'every minute', long: 'minute' },
  [Frequency.HOURLY]: { adverb: 'hourly', long: 'hour' },
  [Frequency.DAILY]: { adverb: 'daily', long: 'day' },
  [Frequency.WEEKLY]: { adverb: 'weekly', long: 'week' },
  [Frequency.MONTHLY]: { adverb: 'monthly', long: 'month' },
  [Frequency.QUARTERLY]: { adverb: 'quarterly', long: 'quarter' },
  [Frequency.BIANNUALLY]: { adverb: 'every 6 months', long: 'half-year' },
  [Frequency.YEARLY]: { adverb: 'yearly', long: 'year' },
}

const pluralize = (n: number, label: string): string =>
  n === 1 ? label : `${label}s`

const buildDelayPhrase = (
  triggerDelay: number,
  triggerDelayType: string
): string | null => {
  const unit = DELAY_UNIT_LABEL[triggerDelayType]
  if (!unit) return null
  return `${triggerDelay} ${pluralize(triggerDelay, unit.long)} delay`
}

const buildCadencePhrase = (
  frequency: string,
  frequencyInterval: number | null | undefined
): string | null => {
  const unit = FREQUENCY_UNIT_LABEL[frequency]
  if (!unit) return null
  const interval =
    frequencyInterval && frequencyInterval > 0 ? frequencyInterval : 1
  if (interval === 1) {
    return `repeats ${unit.adverb}`
  }
  return `repeats every ${interval} ${pluralize(interval, unit.long)}`
}

const buildSummary = ({
  triggerDelay,
  triggerDelayType,
  frequency,
  frequencyInterval,
  executionLimit,
}: {
  triggerDelay: number | null | undefined
  triggerDelayType: string | null | undefined
  frequency: string | null | undefined
  frequencyInterval: number | null | undefined
  executionLimit: number | null | undefined
}): string => {
  const hasDelay =
    triggerDelay != null && triggerDelay > 0 && !!triggerDelayType
  const hasCadence = !!frequency && frequency !== Frequency.ONCE

  const parts: string[] = []

  if (hasDelay) {
    const phrase = buildDelayPhrase(triggerDelay as number, triggerDelayType as string)
    if (phrase) parts.push(phrase)
  }

  if (hasCadence) {
    const phrase = buildCadencePhrase(frequency as string, frequencyInterval)
    if (phrase) parts.push(phrase)
  } else if (!hasDelay) {
    // No delay AND no cadence → fires immediately, exactly once
    return 'Runs immediately · once'
  } else {
    // Delay-only → still fires only once
    parts.push('runs once')
  }

  const cap = executionLimit && executionLimit > 0 ? executionLimit : null
  if (cap && hasCadence) {
    parts.push(`max ${cap}`)
  }

  return parts.join(' · ')
}

type ScheduleTagProps = {
  triggerDelay: number | null | undefined
  triggerDelayType: string | null | undefined
  frequency: string | null | undefined
  frequencyInterval: number | null | undefined
  executionLimit: number | null | undefined
  expanded: boolean
  onToggleExpand: () => void
  children?: ReactNode
}

const ScheduleTag = ({
  triggerDelay,
  triggerDelayType,
  frequency,
  frequencyInterval,
  executionLimit,
  expanded,
  onToggleExpand,
  children,
}: ScheduleTagProps) => {
  const summary = buildSummary({
    triggerDelay,
    triggerDelayType,
    frequency,
    frequencyInterval,
    executionLimit,
  })

  return (
    <div className="relative pt-3">
      {/* Dashed top divider; the floating tag sits centered above it. */}
      <div className="border-t border-dashed border-doubleNickel-gray-300" />

      {/* Floating tag (centered horizontally over the dashed divider). */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-doubleNickel-gray-200 bg-white px-2.5 py-1 text-[10.5px] font-medium text-doubleNickel-gray-900">
          <IconClock className={`${ICON_CLASS} text-doubleNickel-gray-600`} />
          {summary}
        </span>
      </div>

      {/* Schedule details / Edit affordance row */}
      <div className="mt-6 rounded-md border border-dashed border-doubleNickel-gray-300 bg-doubleNickel-gray-50 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-doubleNickel-gray-700">
            Schedule details
          </span>
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-[11px] font-medium text-doubleNickel-brand-700 hover:text-doubleNickel-brand-800"
          >
            {expanded ? 'Hide schedule' : 'Edit schedule →'}
          </button>
        </div>

        {expanded ? <div className="mt-3">{children}</div> : null}
      </div>
    </div>
  )
}

export default ScheduleTag
